import { ComponentProps } from 'react';

import { JitsuProvider } from '@jitsu/jitsu-react';
import * as Sentry from '@sentry/react';
import { QueryClient } from '@tanstack/react-query';
import { createRouter } from '@tanstack/react-router';

import { getWebApp } from '@tg-web/utils';

import { APP_RELEASE } from '../shared/lib/appVersion';
import { reloadOnPreloadError } from '../shared/lib/staleChunk';
import { routeTree } from './routeTree.gen';

// Reloads past a route chunk a deploy replaced -- see `reloadOnPreloadError`
// in `shared/lib/staleChunk.ts`, where it lives so its spec can drive it.
// Registered here because this module is `main.tsx`'s first import, so the
// listener is in place before any route can ask for a chunk.
window.addEventListener('vite:preloadError', reloadOnPreloadError);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Don't retry what will not change. A 4xx is a decision the API has
      // already made -- the draft is gone, the field is invalid, the token is
      // stale -- so three further attempts only delay the error the user is
      // going to see anyway. They also multiplied every such failure by four
      // in Sentry, which is half of why this project became unreadable.
      // Anything else (5xx, transport failures) keeps the default 3 retries.
      retry: (failureCount, error) => {
        const status = (error as Partial<{ status: unknown }> | null)?.status;

        if (typeof status === 'number' && status >= 400 && status < 500) {
          return false;
        }

        return failureCount < 3;
      },
    },
  },
});

// Create a new router instance
const router = createRouter({
  context: {
    queryClient,
  },
  defaultPendingMs: 300,
  defaultPreload: 'intent',
  // Since we're using React Query, we don't want loader calls to ever be stale
  // This will ensure that the loader is always called when the route is preloaded or visited
  defaultPreloadStaleTime: 0,
  routeTree,
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

Sentry.init({
  beforeSend: (event, hint) => {
    // A 4xx is this API working as designed, and calendarFetcher already
    // declines to report them. But it still throws, so callers can handle
    // them, and any rejection that goes unhandled reaches Sentry anyway
    // through the browser's global handler -- which is how the junk bucket
    // survived #161 (TELECAL-WEB-APP-14F). Dropping them here covers every
    // capture route at once, explicit or not.
    const status = (
      hint?.originalException as Partial<{ status: unknown }> | undefined
    )?.status;

    if (typeof status === 'number' && status >= 400 && status < 500) {
      return null;
    }

    // Pass telegram data to events
    if (getWebApp()) {
      const modifiedEvent = { ...event };
      modifiedEvent.extra = {
        ...(event.extra ?? {}),
        tgWebAppInfo: {
          auth_date: getWebApp().initDataUnsafe.auth_date,
          colorScheme: getWebApp().colorScheme,
          language_code: getWebApp().initDataUnsafe.user?.language_code,
          platform: getWebApp().platform,
          start_param: getWebApp().initDataUnsafe.start_param,
          themeParams: getWebApp().themeParams,
          user_id: getWebApp().initDataUnsafe.user?.id,
          username: getWebApp().initDataUnsafe.user?.username,
          version: getWebApp().version,
          viewportHeight: getWebApp().viewportHeight,
        },
      };

      return modifiedEvent;
    }

    return event;
  },
  dsn: 'https://90e16d5ad8fe965c23b08824f8dfd8cb@o4508335988473856.ingest.de.sentry.io/4508360380383312',
  // Disabled in cbf9376 (2025-12-23) "из-за заморозки"; re-enabled 2026-09-04.
  // That commit was the frontend half of a December cut across both repos. The
  // backend half (tgcal_backend#350, six days earlier) turned off logs, traces
  // and profiles and deliberately kept errors — this one reached for `enabled:
  // false`, which took errors with it. Every captureException in the app has
  // gone nowhere since: ErrorPage (the root errorComponent behind the "Упс /
  // Календарь потерялся во времени" screen users report), getStartappParam, and
  // the three Google auth sites.
  //
  // Errors are not what costs money here. Measured 2026-09-04: 111 errors used
  // against 50,000 reserved on the plan, while the quota goes on the backend's
  // cron check-ins. See "Sentry's quota goes on cron check-ins" in
  // tgcal_backend/CLAUDE.md — there is room to report more, not less.
  enabled: import.meta.env.VITE_APP_ENV !== 'development',
  environment: import.meta.env.VITE_APP_ENV,
  // httpClientIntegration is deliberately absent. Every request in this app
  // goes through calendarFetch, which already reports 5xx with the endpoint
  // template, the payload and a fingerprint -- so the integration only ever
  // filed a second, thinner copy of the same failure ("HTTP Client Error with
  // status code: 500", TELECAL-WEB-APP-14E duplicating 14G).
  integrations: [Sentry.tanstackRouterBrowserTracingIntegration(router)],

  normalizeDepth: 10,

  // The full commit SHA, from index.html. @sentry/vite-plugin used to inject it
  // into every module, which renamed every chunk on every deploy; see
  // vite.config.ts.
  release: APP_RELEASE,

  // This option is required for capturing headers and cookies.
  sendDefaultPii: true,

  // Errors go unsampled; performance data does not.
  // tanstackRouterBrowserTracingIntegration sends a transaction per route
  // change and Sentry bills the spans inside them, so 1.0 was the real quota
  // risk in this file — an estimated 6–18M spans/month against 5M reserved,
  // which would have spilled into on-demand spend. 0.05 keeps it under a
  // million and still says whether a route got slower. The backend runs 0.
  tracesSampleRate: import.meta.env.VITE_APP_ENV === 'development' ? 0 : 0.05,
});

const jitsuOptions: ComponentProps<typeof JitsuProvider>['options'] = {
  echoEvents: import.meta.env.VITE_APP_ENV === 'development',
  host: import.meta.env.VITE_JITSU_HOST,
  writeKey: import.meta.env.VITE_JITSU_WRITE_KEY,
};

export { jitsuOptions, queryClient, router };
